import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PropertyCard from '../components/property/PropertyCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faHome, faBuilding, faHandshake, faEye, faBullseye, faTools, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import missionImg from '../assets/images/missionImg.jpg';
import './HomePage.css';

// Sample property data (in a real app, this would come from an API)
const featuredProperties = [
  {
    id: 1,
    title: 'Modern Apartment with Ocean View',
    address: '123 Coastal Drive, Miami, FL',
    price: 450000,
    type: 'For Sale',
    bedrooms: 2,
    bathrooms: 2,
    area: 1200,
    image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
  },
  {
    id: 2,
    title: 'Luxury Villa with Pool',
    address: '456 Palm Avenue, Los Angeles, CA',
    price: 1250000,
    type: 'For Sale',
    bedrooms: 4,
    bathrooms: 3.5,
    area: 3200,
    image: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
  },
  {
    id: 3,
    title: 'Downtown Loft',
    address: '789 Urban Street, New York, NY',
    price: 3500,
    type: 'For Rent',
    bedrooms: 1,
    bathrooms: 1,
    area: 950,
    image: 'https://images.unsplash.com/photo-1554995207-c18c203602cb?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
  }
];

const HomePage = () => {
  const [heroData, setHeroData] = useState({
    title: 'Find Your Dream Home',
    subtitle: 'Discover the perfect property that matches your lifestyle and preferences',
    image_path: null,
    mobile_image_path: null,
    tablet_image_path: null
  });
  
  const [carouselImages, setCarouselImages] = useState([]);
  const [desktopCarouselImages, setDesktopCarouselImages] = useState([]);
  const [tabletCarouselImages, setTabletCarouselImages] = useState([]);
  const [mobileCarouselImages, setMobileCarouselImages] = useState([]);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [useCarousel, setUseCarousel] = useState(false);
  const carouselIntervalRef = useRef(null);
  const [screenSize, setScreenSize] = useState('desktop');

  const [visionData, setVisionData] = useState({
    header: 'Our Vision',
    paragraph: '',
    image_path: null
  });

  const [missionData, setMissionData] = useState({
    header: 'Our Mission',
    paragraph: '',
    image_path: null
  });

  // Function to determine screen size
  const determineScreenSize = () => {
    const width = window.innerWidth;
    if (width <= 768) {
      return 'mobile';
    } else if (width <= 1024) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  };

  useEffect(() => {
    fetchHeroData();
    fetchVisionData();
    fetchMissionData();
    
    // Set initial screen size
    setScreenSize(determineScreenSize());
    
    // Add resize event listener
    const handleResize = () => {
      setScreenSize(determineScreenSize());
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      if (carouselIntervalRef.current) {
        clearInterval(carouselIntervalRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Set up carousel auto-rotation
  useEffect(() => {
    if (useCarousel && carouselImages.length > 1) {
      carouselIntervalRef.current = setInterval(() => {
        setCurrentCarouselIndex(prevIndex => 
          prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000); // Change image every 5 seconds
    }
    
    return () => {
      if (carouselIntervalRef.current) {
        clearInterval(carouselIntervalRef.current);
      }
    };
  }, [useCarousel, carouselImages.length]);
  
  const handleCarouselPrev = () => {
    setCurrentCarouselIndex(prevIndex => 
      prevIndex === 0 ? carouselImages.length - 1 : prevIndex - 1
    );
  };
  
  const handleCarouselNext = () => {
    setCurrentCarouselIndex(prevIndex => 
      prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const fetchHeroData = async () => {
    try {
      const response = await axios.get('https://realestate.bonlineco.com/api/hero');
      console.log('Hero API response:', response.data); // Debug log
      
      if (response.data.status === 'success') {
        // Set hero data
        if (response.data.data) {
          setHeroData({
            title: response.data.data.title || 'Find Your Dream Home',
            subtitle: response.data.data.subtitle || 'Discover the perfect property that matches your lifestyle and preferences',
            image_path: response.data.data.image_path,
            mobile_image_path: response.data.data.mobile_image_path,
            tablet_image_path: response.data.data.tablet_image_path
          });
        }
        
        // Set all carousel images
        if (response.data.carousel_images && response.data.carousel_images.length > 0) {
          console.log('Carousel images found:', response.data.carousel_images.length);
          setCarouselImages(response.data.carousel_images);
        } else {
          console.log('No carousel images found');
          setCarouselImages([]);
        }
        
        // Set device-specific carousel images
        if (response.data.desktop_carousel_images) {
          console.log('Desktop carousel images found:', response.data.desktop_carousel_images.length);
          setDesktopCarouselImages(response.data.desktop_carousel_images);
        }
        
        if (response.data.tablet_carousel_images) {
          console.log('Tablet carousel images found:', response.data.tablet_carousel_images.length);
          setTabletCarouselImages(response.data.tablet_carousel_images);
        }
        
        if (response.data.mobile_carousel_images) {
          console.log('Mobile carousel images found:', response.data.mobile_carousel_images.length);
          setMobileCarouselImages(response.data.mobile_carousel_images);
        }
        
        // Check if use_carousel is explicitly set
        if (response.data.data && response.data.data.use_carousel !== undefined) {
          console.log('use_carousel setting:', response.data.data.use_carousel);
          // Use the explicit setting from the backend
          setUseCarousel(response.data.data.use_carousel);
        } else {
          // Fall back to the old logic if use_carousel is not set
          if (response.data.carousel_images && response.data.carousel_images.length > 0) {
            // If we have carousel images, enable carousel
            setUseCarousel(true);
          } else {
            // No carousel images, use single image
            setUseCarousel(false);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch hero data:', error);
    }
  };

  const fetchVisionData = async () => {
    try {
      const response = await axios.get('https://realestate.bonlineco.com/api/vision');
      if (response.data.status === 'success' && response.data.data) {
        setVisionData({
          header: 'Our Vision',
          paragraph: response.data.data.paragraph || '',
          image_path: response.data.data.image_path
        });
      }
    } catch (error) {
      console.error('Failed to fetch vision data:', error);
    }
  };

  const fetchMissionData = async () => {
    try {
      const response = await axios.get('https://realestate.bonlineco.com/api/mission');
      if (response.data.status === 'success' && response.data.data) {
        setMissionData({
          header: 'Our Mission',
          paragraph: response.data.data.paragraph || '',
          image_path: response.data.data.image_path
        });
      }
    } catch (error) {
      console.error('Failed to fetch mission data:', error);
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        {useCarousel && (() => {
          // Determine which carousel images to use based on screen size
          const currentImages = screenSize === 'mobile' && mobileCarouselImages.length > 0 ? mobileCarouselImages :
                               screenSize === 'tablet' && tabletCarouselImages.length > 0 ? tabletCarouselImages :
                               desktopCarouselImages.length > 0 ? desktopCarouselImages : carouselImages;
          
          return currentImages.length > 0 ? (
          <div className="hero-carousel">
            {currentImages.map((image, index) => (
              <div 
                key={image.id} 
                className={`hero-carousel-item ${index === currentCarouselIndex ? 'active' : ''}`}
                style={{
                  backgroundImage: (() => {
                    // Choose the appropriate image based on screen size and availability
                    if (screenSize === 'mobile' && image.mobile_image_path) {
                      return `url(https://realestate.bonlineco.com/api/image/${image.mobile_image_path.replace(/\\/g, '/')})`;  
                    } else if (screenSize === 'tablet' && image.tablet_image_path) {
                      return `url(https://realestate.bonlineco.com/api/image/${image.tablet_image_path.replace(/\\/g, '/')})`;  
                    } else {
                      return `url(https://realestate.bonlineco.com/api/image/${image.image_path.replace(/\\/g, '/')})`;  
                    }
                  })()
                }}
              ></div>
            ))}
            
            {currentImages.length > 1 && (
              <>
                <button className="carousel-control carousel-control-prev" onClick={handleCarouselPrev}>
                  <FontAwesomeIcon icon={faChevronLeft} size="2x" />
                </button>
                <button className="carousel-control carousel-control-next" onClick={handleCarouselNext}>
                  <FontAwesomeIcon icon={faChevronRight} size="2x" />
                </button>
                <div className="carousel-indicators" style={{position: 'absolute', bottom: '20px', left: '0', right: '0', textAlign: 'center', width: '100%'}}>
                  <div style={{display: 'inline-block'}}>
                    {currentImages.map((_, index) => (
                      <button 
                        key={index}
                        className={`carousel-indicator ${index === currentCarouselIndex ? 'active' : ''}`}
                        onClick={() => setCurrentCarouselIndex(index)}
                        style={{display: 'inline-block', margin: '0 5px', width: '12px', height: '12px', borderRadius: '50%', background: index === currentCarouselIndex ? '#fff' : 'rgba(255, 255, 255, 0.5)', border: 'none'}}
                      ></button>
                    ))}
                  </div>
                </div>
              </>
            )}
            
            <div className="container">
              <div className="hero-content">
                <h1>{heroData.title}</h1>
                <p>{heroData.subtitle}</p>
                <Link to="/properties" className="btn">Get Started</Link>
              </div>
            </div>
          </div>
          ) : null;
        })() || (
          <div className="hero-background" style={{
            backgroundImage: (() => {
              // Choose the appropriate image based on screen size
              if (screenSize === 'mobile' && heroData.mobile_image_path) {
                return `url(https://realestate.bonlineco.com/api/image/${heroData.mobile_image_path.replace(/\\/g, '/')})`;  
              } else if (screenSize === 'tablet' && heroData.tablet_image_path) {
                return `url(https://realestate.bonlineco.com/api/image/${heroData.tablet_image_path.replace(/\\/g, '/')})`;  
              } else if (heroData.image_path) {
                return `url(https://realestate.bonlineco.com/api/image/${heroData.image_path.replace(/\\/g, '/')})`;  
              } else {
                return 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1973&q=80)';
              }
            })()
          }}>
            <div className="container">
              <div className="hero-content">
                <h1>{heroData.title}</h1>
                <p>{heroData.subtitle}</p>
                <Link to="/properties" className="btn">Get Started</Link>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Vision Section */}
      <section className="vision-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <div className="vision-image">
                <img 
                  src={visionData.image_path ? `https://realestate.bonlineco.com/api/image/${visionData.image_path.replace(/\\/g, '/')}` : 'https://via.placeholder.com/800x400'} 
                  alt="Our Vision" 
                  className="img-fluid" 
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="vision-content">
                <div className="icon-container mb-4">
                  <FontAwesomeIcon icon={faEye} size="3x" className="text-primary" />
                </div>
                <h2 className="mb-4">{visionData.header}</h2>
                <p className="lead">{visionData.paragraph || 'Loading vision content...'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <div className="mission-content">
                <div className="icon-container mb-4">
                  <FontAwesomeIcon icon={faBullseye} size="3x" className="text-primary" />
                </div>
                <h2 className="mb-4">{missionData.header}</h2>
                <p className="lead">{missionData.paragraph || 'Loading mission content...'}</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mission-image">
                <img 
                  src={missionData.image_path ? `https://realestate.bonlineco.com/api/image/${missionData.image_path.replace(/\\/g, '/')}` : 'https://via.placeholder.com/800x400'} 
                  alt="Our Mission" 
                  className="img-fluid" 
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="featured-properties py-5">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2>Featured Properties</h2>
            <p>Explore our handpicked selection of premium properties</p>
          </div>
          <div className="row">
            {featuredProperties.map(property => (
              <div className="col-md-4" key={property.id}>
                <PropertyCard property={property} />
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/properties" className="btn btn-outline-primary">View All Properties</Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services py-5 bg-light">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2>Our Services</h2>
            <p>We provide comprehensive real estate services to meet your needs</p>
          </div>
          <div className="row">
            <div className="col-md-3">
              <div className="service-card text-center p-4">
                <div className="icon-container mb-3">
                  <FontAwesomeIcon icon={faHome} size="3x" className="service-icon" />
                </div>
                <h3>Buy a Property</h3>
                <p>Find your dream home with our extensive listings and expert guidance throughout the buying process.</p>
                <div className="mt-3">
                  <Link to="/properties" className="btn-service-more">
                    View More <FontAwesomeIcon icon={faEye} className="btn-icon" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="service-card text-center p-4">
                <div className="icon-container mb-3">
                  <FontAwesomeIcon icon={faBuilding} size="3x" className="service-icon" />
                </div>
                <h3>Sell a Property</h3>
                <p>Maximize your property's value with our market analysis, strategic pricing, and effective marketing.</p>
                <div className="mt-3">
                  <Link to="/contact" className="btn-service-more">
                    View More <FontAwesomeIcon icon={faEye} className="btn-icon" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="service-card text-center p-4">
                <div className="icon-container mb-3">
                  <FontAwesomeIcon icon={faHandshake} size="3x" className="service-icon" />
                </div>
                <h3>Property Management</h3>
                <p>Let us handle the day-to-day operations of your investment property for a hassle-free experience.</p>
                <div className="mt-3">
                  <Link to="/about" className="btn-service-more">
                    View More <FontAwesomeIcon icon={faEye} className="btn-icon" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="service-card text-center p-4">
                <div className="icon-container mb-3">
                  <FontAwesomeIcon icon={faTools} size="3x" className="service-icon" />
                </div>
                <h3>Maintenance Request</h3>
                <p>Submit and track maintenance requests easily for prompt resolution of any property issues.</p>
                <div className="mt-3">
                  <Link to="/maintenance-request" className="btn-service-more">
                    View More <FontAwesomeIcon icon={faEye} className="btn-icon" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
